import { useState } from "react";
import {
  type GridColumnProps,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { useRouteContext } from "@tanstack/react-router";
import { FiPlus } from "react-icons/fi";

import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { type SCLR } from "@/models";
import type { STFF } from "@/models/stff";
import { useLeaData } from "@/queries.ts";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import SCLREditDialog from "../dialogs/SCLREditDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalStaffId", field: "LocalStaffId", title: "Staff Local ID" },
  { id: "ClassroomName", field: "ClassroomName", title: "Classroom Name" },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Staff Local ID", key: "LocalStaffId" },
  { label: "Classroom Name", key: "ClassroomName" },
];

type DialogState =
  | { mode: "closed" }
  | { mode: "add"; defaults: Partial<SCLR> }
  | { mode: "edit"; record: SCLR };

export default function SCLRRecordsTab({
  sclrRecords,
  staff,
}: {
  sclrRecords: SCLR[];
  staff: STFF;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });

  const closeDialog = () => setDialog({ mode: "closed" });

  const onAddButtonClick = () => {
    const sassPreschool =
      leaData?.sass?.find(
        (sass) =>
          sass.LocalStaffId === staff.LocalStaffId &&
          sass.ProgramYear === staff.ProgramYear
      )?.PreschoolCode ||
      "";

    setDialog({
      mode: "add",
      defaults: {
        LocalStaffId: staff.LocalStaffId ?? "",
        PreschoolCode: sassPreschool,
        ProgramYear: staff.ProgramYear ?? "",
      },
    });
  };

  const onRowClick = (c: SCLR) => {
    setDialog({ mode: "edit", record: c });
  };

  const canEdit = leaContext.hasRole("SCLREdit");

  const staffName = [
    staff.StaffLegalFirstName,
    staff.StaffLegalMiddleName,
    staff.StaffLegalLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData = sclrRecords.map((record) => ({
    ...record,
    StaffName: staffName,
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3 px-3">
        <TooltipTrigger tooltipText="Export staff classroom records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["StaffName"],
              "staff-classroom-record"
            )}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a staff classroom record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>
      <Card className="mb-3 shadow-none">
        <KendoTableClientState
          data={tableData}
          columns={columns}
          onRowClick={(event: GridRowClickEvent) => {
            onRowClick(event.dataItem);
          }}
        />
      </Card>

      {dialog.mode === "add" && (
        <SCLREditDialog defaults={dialog.defaults} onClose={closeDialog} />
      )}
      {dialog.mode === "edit" && (
        <SCLREditDialog record={dialog.record} onClose={closeDialog} />
      )}
    </>
  );
}
